import React from 'react';
import { Col, List, Row } from 'antd';
import { FormattedMessage, useIntl } from 'react-intl';
import { useReaders } from 'ShorePay/hooks/readers';
import styled from 'styled-components';
import CardReaderIcon from 'assets/images/icons/card-reader.svg';
import EmptyPlaceHolderImg from 'assets/images/shorepay_no_readers_placeholder.png';
import { Reader } from 'utils/apiClient/services/shorepay/types';
import ReaderStatusTag from './ReaderStatusTag';

const ListItem = styled(List.Item)`
  border: 1px solid rgba(0, 0, 0, 0.06);
  border-radius: 4px;
  box-sizing: border-box;
  margin: 8px 0;
  padding: 7px 0;
`;

const ListItemMeta = styled(List.Item.Meta)`
  height: 100%;
  .ant-list-item-meta-avatar {
    margin: 0 28px;
    height: 100%;
    display: flex;
    align-items: center;
  }
  .ant-list-item-meta-title,
  .ant-list-item-meta-description {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

const ReadersListItem: React.FC<{ data: Reader }> = ({ data }) => {
  const intl = useIntl();
  return (
    <Row css="width: 100%">
      <Col flex="0 0 320px" css="padding-right: 10px">
        <ListItemMeta
          avatar={<CardReaderIcon />}
          title={intl.formatMessage(
            {
              defaultMessage: 'Reader ({reader_name})',
            },
            { reader_name: data.name }
          )}
          description={<span>Reader ID {data.reader_id}</span>}
        />
      </Col>
      <Col flex="auto">
        <Row css="height: 100%; margin-right: 40px" justify="space-between">
          <span>
            {data.last_active && (
              <FormattedMessage
                defaultMessage="Last Active: {date_time}"
                values={{
                  date_time: `${intl.formatDate(
                    data.last_active
                  )} ${intl.formatTime(data.last_active)}`,
                }}
              />
            )}
          </span>
          <div css="display: flex; align-items: center">
            <ReaderStatusTag status={data.status} />
          </div>
        </Row>
      </Col>
    </Row>
  );
};

const ReadersList: React.FC = () => {
  const readersQuery = useReaders();

  if (readersQuery.isSuccess) {
    const { data } = readersQuery;

    if (data.length === 0) {
      return (
        <>
          <Row justify="center">
            <img
              css="height: 160px"
              src={EmptyPlaceHolderImg}
              alt="empty box"
            />
          </Row>
          <Row justify="center">
            <span css="color: rgba(0, 0, 0, 0.25); font-weight: 500">
              <FormattedMessage defaultMessage="No readers yet. Order one now!" />
            </span>
          </Row>
        </>
      );
    }
    return (
      <List<Reader>
        itemLayout="horizontal"
        dataSource={data}
        split={false}
        rowKey={(r) => r.id.toString()}
        renderItem={(reader) => (
          <ListItem>
            <ReadersListItem data={reader} />
          </ListItem>
        )}
      />
    );
  }

  return null;
};

export default ReadersList;
